import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Sparkles, PenLine, Share2, MessageCircle, TrendingUp, Briefcase, Search, Users, Utensils, Scale } from 'lucide-react';
import { Button } from '../components/ui/button';
import SEO from '../components/SEO';
import { TEAM_AVATARS } from '../components/teamAvatars';
import { useT } from '../i18n';

const avatarFor = (key) => TEAM_AVATARS.find((a) => a.key === key)?.img || null;

const PLATFORM_CHIPS = {
  indexai: { name: 'Index AI™', href: '/index-ai' },
  workforce: { name: 'AI Workforce™', href: '/ai-agents-for-restaurants' },
  catering: { name: 'Catering AI™', href: '/ai/catering-ai' },
};

export const AI_TEAM = [
  {
    slug: 'penny',
    name: 'Penny',
    icon: PenLine,
    avatar: avatarFor('penny'),
    platforms: ['indexai'],
    active: true,
    authorPage: '/magazine/author/penny',
    role: { en: 'Editorial Director', es: 'Directora Editorial' },
    desc: {
      en: 'Researches the restaurant industry daily and writes every Knowledge Center article — POS economics, AI search, channel strategy.',
      es: 'Investiga la industria restaurantera a diario y escribe cada artículo del Knowledge Center — economía de POS, búsqueda con IA, estrategia de canales.',
    },
    duties: {
      en: ['Industry research', 'Long-form articles', 'SEO & AI-search optimization'],
      es: ['Investigación de industria', 'Artículos de fondo', 'SEO y optimización para búsqueda IA'],
    },
  },
  {
    slug: 'sonny',
    name: 'Sonny',
    icon: Share2,
    avatar: avatarFor('sonny'),
    platforms: ['indexai'],
    active: true,
    authorPage: '/magazine/author/sonny',
    role: { en: 'Distribution Director', es: 'Director de Distribución' },
    desc: {
      en: 'Turns every article into the right format for LinkedIn, newsletter, Google Business and partner channels.',
      es: 'Convierte cada artículo al formato correcto para LinkedIn, newsletter, Google Business y canales aliados.',
    },
    duties: {
      en: ['Social distribution', 'Newsletter', 'Channel formatting'],
      es: ['Distribución social', 'Newsletter', 'Formatos por canal'],
    },
  },
  {
    slug: 'rachel',
    name: 'Rachel',
    icon: MessageCircle,
    avatar: avatarFor('rachel'),
    platforms: ['workforce'],
    active: true,
    authorPage: '/contact',
    role: { en: 'Customer Experience', es: 'Experiencia de Cliente' },
    desc: {
      en: 'Your concierge. Answers every inquiry, guides free audits and makes sure no restaurant waits for help.',
      es: 'Tu concierge. Responde cada consulta, guía las auditorías gratuitas y se asegura de que ningún restaurante espere por ayuda.',
    },
    duties: {
      en: ['Free audit intake', 'Owner support', 'Onboarding'],
      es: ['Recepción de auditorías', 'Soporte a dueños', 'Onboarding'],
    },
  },
  {
    slug: 'stan',
    name: 'Stan',
    icon: TrendingUp,
    avatar: avatarFor('stan'),
    platforms: ['catering'],
    active: true,
    role: { en: 'Growth & Lead Generation', es: 'Crecimiento y Generación de Leads' },
    desc: {
      en: 'Finds restaurants that need help before they even search — outreach, partnerships and growth loops.',
      es: 'Encuentra restaurantes que necesitan ayuda antes de que busquen — outreach, alianzas y ciclos de crecimiento.',
    },
    duties: { en: ['Outbound growth', 'Partnerships'], es: ['Crecimiento outbound', 'Alianzas'] },
  },
  {
    slug: 'eva',
    name: 'Eva',
    icon: Briefcase,
    avatar: avatarFor('eva'),
    platforms: ['workforce'],
    active: true,
    role: { en: 'Executive Operations', es: 'Operaciones Ejecutivas' },
    desc: {
      en: 'Coordinates the AI team, tracks KPIs and keeps the whole operating system accountable.',
      es: 'Coordina al equipo de IA, sigue los KPIs y mantiene todo el sistema operativo bajo control.',
    },
    duties: { en: ['Team coordination', 'KPIs & reporting'], es: ['Coordinación del equipo', 'KPIs y reportes'] },
  },
  {
    slug: 'linda',
    name: 'Linda',
    icon: Scale,
    avatar: avatarFor('linda'),
    platforms: ['workforce'],
    active: true,
    role: { en: 'Legal & Compliance', es: 'Legal y Cumplimiento' },
    desc: {
      en: 'Prepares operational documents, reviews supplier agreements and keeps your paperwork audit-ready.',
      es: 'Prepara documentos operativos, revisa contratos de proveedores y mantiene tu papeleo listo para auditoría.',
    },
    duties: { en: ['Operational documents', 'Supplier agreements'], es: ['Documentos operativos', 'Contratos de proveedores'] },
  },
];

function MemberCard({ m, isEs }) {
  const Icon = m.icon;
  const inner = (
    <div className={`h-full rounded-3xl border p-7 transition-all ${m.active ? 'border-neutral-200 bg-white hover:border-[#F77F11] hover:shadow-[0_30px_70px_-30px_rgba(247,127,17,0.45)] hover:-translate-y-1' : 'border-dashed border-neutral-300 bg-neutral-50/60'}`}>
      <div className="flex items-center gap-4">
        {m.avatar ? (
          <img src={m.avatar} alt={m.name} className="w-16 h-16 rounded-2xl object-cover ring-2 ring-orange-100" />
        ) : (
          <div className={`w-16 h-16 rounded-2xl flex items-center justify-center text-xl font-bold ${m.active ? 'bg-[#F77F11] text-white' : 'bg-neutral-200 text-neutral-500'}`}>
            {m.name.charAt(0)}
          </div>
        )}
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="text-lg font-bold text-neutral-900">{m.name}</h3>
            {!m.active && (
              <span className="px-2 py-0.5 rounded-full bg-neutral-200 text-neutral-500 text-[10px] font-semibold uppercase tracking-wide">
                {isEs ? 'Pronto' : 'Soon'}
              </span>
            )}
          </div>
          <p className="text-[12px] font-semibold text-[#F77F11] uppercase tracking-wider">{isEs ? m.role.es : m.role.en}</p>
        </div>
      </div>
      <p className="mt-4 text-[14px] text-neutral-600 leading-relaxed">{isEs ? m.desc.es : m.desc.en}</p>
      <div className="mt-4 flex flex-wrap gap-1.5">
        {(isEs ? m.duties.es : m.duties.en).map((d) => (
          <span key={d} className="px-2.5 py-1 rounded-full bg-orange-50 text-[#b45309] text-[11px] font-medium">{d}</span>
        ))}
      </div>
      {m.platforms?.length > 0 && (
        <div className="mt-4 pt-4 border-t border-neutral-100">
          <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-neutral-400">
            {isEs ? 'Interviene en' : 'Works in'}
          </p>
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            {m.platforms.map((p) => (
              <span key={p} className="px-2.5 py-1 rounded-full border border-[#F77F11]/40 bg-white text-[#F77F11] text-[11px] font-semibold">
                {PLATFORM_CHIPS[p].name}
              </span>
            ))}
          </div>
        </div>
      )}
      {m.active && m.authorPage && (
        <span className="mt-5 inline-flex items-center gap-1 text-sm font-semibold text-[#F77F11]">
          <Icon className="w-4 h-4" /> {isEs ? 'Ver perfil' : 'View profile'} <ArrowRight className="w-3.5 h-3.5" />
        </span>
      )}
    </div>
  );
  return m.active && m.authorPage ? (
    <Link to={m.authorPage} data-testid={`ai-team-card-${m.slug}`} className="block h-full">{inner}</Link>
  ) : (
    <div data-testid={`ai-team-card-${m.slug}`} className="h-full">{inner}</div>
  );
}

export default function AiTeamPage() {
  const { lang } = useT();
  const isEs = lang === 'es';
  return (
    <div className="pt-32 pb-24 bg-[#FAFAF7] min-h-screen" data-testid="ai-team-page">
      <SEO
        title={isEs ? 'Conoce al Equipo IA de Kitxens' : 'Meet the KITXENS AI Operating Team'}
        description={isEs ? 'Penny escribe, Sonny distribuye, Rachel atiende. El primer equipo editorial y operativo de IA de la industria restaurantera.' : 'Penny writes, Sonny distributes, Rachel takes care of you. The restaurant industry’s first AI editorial and operating team.'}
        path="/ai-team"
      />
      <div className="max-w-[1180px] mx-auto px-6 lg:px-10">
        <div className="text-center max-w-3xl mx-auto">
          <p className="kx-eyebrow mb-4 inline-flex items-center gap-2"><Sparkles className="w-3.5 h-3.5" /> {isEs ? 'EQUIPO OPERATIVO DE IA' : 'AI OPERATING TEAM'}</p>
          <h1 className="text-4xl sm:text-5xl font-bold text-neutral-900 tracking-tight">
            {isEs ? 'Conoce al Equipo IA de' : 'Meet the AI Team behind'}{' '}
            <span className="bg-gradient-to-r from-[#F77F11] to-[#ff9b4a] bg-clip-text text-transparent pb-[0.12em] -mb-[0.12em]">KITXENS</span>
          </h1>
          <p className="mt-5 text-neutral-600 text-base sm:text-lg leading-relaxed">
            {isEs
              ? 'No son chatbots. Son especialistas de IA con nombre, rol y responsabilidades — trabajando 24/7 para los restaurantes. Supervisados por operadores humanos reales.'
              : 'Not chatbots. Named AI specialists with roles and responsibilities — working 24/7 for restaurants. Supervised by real human operators.'}
          </p>
        </div>
        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {AI_TEAM.map((m) => <MemberCard key={m.slug} m={m} isEs={isEs} />)}
        </div>

        {/* How the team helps restaurants sell more */}
        <div className="mt-20" data-testid="ai-team-sell-more">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <p className="kx-eyebrow mb-4">{isEs ? 'EN ACCIÓN' : 'IN ACTION'}</p>
            <h2 className="text-3xl sm:text-4xl font-bold text-neutral-900 tracking-tight">
              {isEs ? 'Así te ayudan a vender más.' : 'This is how they help you sell more.'}
            </h2>
            <p className="mt-3 text-neutral-600 text-[15px]">
              {isEs
                ? 'Cada agente trabaja dentro de una de nuestras tres plataformas gestionadas.'
                : 'Each agent works inside one of our three managed platforms.'}
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              {
                icon: Search,
                id: 'indexai',
                agents: ['penny', 'sonny'],
                price: 99,
                headline: isEs ? 'Te hacen visible donde la IA recomienda.' : 'They make you visible where AI recommends.',
                examples: isEs
                  ? ['Penny publica contenido que posiciona tu restaurante en ChatGPT y Google AI', 'Sonny lo distribuye en LinkedIn, newsletter y Google Business', 'Resultado: más clientes te encuentran antes que a tu competencia']
                  : ['Penny publishes content that positions your restaurant in ChatGPT and Google AI', 'Sonny distributes it across LinkedIn, newsletter and Google Business', 'Result: more customers find you before your competitors'],
              },
              {
                icon: Users,
                id: 'workforce',
                agents: ['rachel', 'eva'],
                price: 199,
                headline: isEs ? 'Atienden a tus clientes 24/7 sin que pierdas una venta.' : 'They serve your customers 24/7 so you never lose a sale.',
                examples: isEs
                  ? ['Rachel contesta llamadas y WhatsApp al instante — reservas incluidas', 'Responde cada reseña y guía tu auditoría gratuita', 'Eva coordina al equipo y te entrega reportes claros cada mes']
                  : ['Rachel answers calls and WhatsApp instantly — reservations included', 'She replies to every review and guides your free audit', 'Eva coordinates the team and sends you clear monthly reports'],
              },
              {
                icon: Utensils,
                id: 'catering',
                agents: ['stan', 'rachel'],
                price: 399,
                headline: isEs ? 'Convierten consultas en pedidos corporativos grandes.' : 'They turn inquiries into large corporate orders.',
                examples: isEs
                  ? ['Stan encuentra empresas que necesitan catering antes de que busquen', 'Rachel responde cotizaciones y hace seguimiento hasta confirmar', 'Resultado: ingresos de catering recurrentes sin contratar más personal']
                  : ['Stan finds companies that need catering before they even search', 'Rachel answers quotes and follows up until confirmed', 'Result: recurring catering revenue without hiring more staff'],
              },
            ].map((b) => {
              const BIcon = b.icon;
              return (
                <div key={b.id} className="flex flex-col rounded-3xl border border-neutral-200 bg-white p-7 hover:border-[#F77F11]/60 hover:shadow-[0_30px_70px_-40px_rgba(247,127,17,0.4)] transition-all" data-testid={`sell-more-${b.id}`}>
                  <div className="flex items-center justify-between gap-3">
                    <span className="inline-flex items-center gap-2">
                      <span className="w-10 h-10 rounded-xl bg-neutral-950 text-white flex items-center justify-center"><BIcon className="w-5 h-5" /></span>
                      <span className="font-bold text-neutral-900 text-[15.5px]">{PLATFORM_CHIPS[b.id].name}</span>
                    </span>
                    <span className="flex -space-x-2 shrink-0">
                      {b.agents.map((a) => {
                        const img = avatarFor(a);
                        return img
                          ? <img key={a} src={img} alt={a} className="w-8 h-8 rounded-full object-cover ring-2 ring-white" />
                          : <span key={a} className="w-8 h-8 rounded-full bg-[#F77F11] text-white text-[11px] font-bold flex items-center justify-center ring-2 ring-white">{a.charAt(0).toUpperCase()}</span>;
                      })}
                    </span>
                  </div>
                  <h3 className="mt-4 text-[18px] font-bold text-neutral-900 leading-snug">{b.headline}</h3>
                  <ul className="mt-3.5 space-y-2 flex-1">
                    {b.examples.map((x) => (
                      <li key={x} className="flex items-start gap-2 text-[13.5px] text-neutral-600 leading-relaxed">
                        <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#F77F11] shrink-0" /> <span className="min-w-0">{x}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="mt-5 pt-4 border-t border-neutral-100 flex items-center justify-between gap-3">
                    <p className="text-[13px] font-semibold text-neutral-900">{isEs ? `Desde $${b.price}/mes` : `From $${b.price}/mo`}</p>
                    <Link to={PLATFORM_CHIPS[b.id].href} className="inline-flex items-center gap-1 text-[13px] font-semibold text-[#F77F11] hover:text-[#d8651a]" data-testid={`sell-more-link-${b.id}`}>
                      {isEs ? 'Conocer más' : 'Learn more'} <ArrowRight className="w-3.5 h-3.5" />
                    </Link>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        <div className="mt-14 text-center">
          <Button asChild className="bg-[#F77F11] hover:bg-[#d8651a] text-white h-12 px-8 rounded-full text-[15px] font-semibold">
            <Link to="/#audit" data-testid="ai-team-audit-cta">
              {isEs ? 'Pon al equipo a trabajar para ti — Gratis' : 'Put the team to work for you — Free'}
              <ArrowRight className="w-4 h-4 ml-1.5" />
            </Link>
          </Button>
          <p className="mt-4 text-sm text-neutral-500">
            <Link to="/magazine" className="text-[#F77F11] font-semibold hover:underline">{isEs ? '← Lee lo que Penny publica en el Knowledge Center' : '← Read what Penny publishes in the Knowledge Center'}</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
